import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Params } from '@angular/router';
import { NgxGalleryAnimation, NgxGalleryOptions } from '@kolkov/ngx-gallery';
import { take } from 'rxjs/operators';

import { Location, ToursSearch } from 'package-types';
import { Required } from 'package-utils';

import { GalleryLinks, pickGalleryImage } from '@helpers/images.helper';
import { ImagesEndpoint } from '@services/api/images.endpoint';

import { NormalizedParams, NormalizedSearch } from '@interfaces/modules/tours/search';

@Component({
  selector: 'app-hotel-card',
  templateUrl: './hotel-card.component.html',
  styleUrls: ['./hotel-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HotelCardComponent implements OnChanges {
  @ViewChild('carousel', { static: false }) carousel?: ElementRef<HTMLElement>;

  @Required @Input() hotelId!: number;
  @Input() galleryImages: GalleryLinks[] = [];
  @Input() imagesCount?: number;
  @Input() hotelUrl: string = '';
  @Input() hotelName: string = '';
  @Input() stars: string | number = 0;
  @Input() rating: number = 0;
  @Input() countryName: string = '';
  @Input() resortName: string = '';
  @Input() location?: Location;

  @Input() offer?: ToursSearch.Offer;

  // These fields have a higher display priority than offer @input
  @Input() food?: ToursSearch.Food;
  @Input() accommodation?: ToursSearch.Accommodation;
  @Input() transport?: ToursSearch.Transport;
  @Input() room?: string;
  @Input() priceUAH?: string;

  queryParams: Params = { };
  options: NgxGalleryOptions[] = [{
    thumbnails: false,
    preview: false,
    imageAnimation: NgxGalleryAnimation.Slide
  }];
  loadingGalleryImages = false;
  fullGalleryLoaded = false;

  constructor(
    private readonly modalService: NgbModal,
    private readonly route: ActivatedRoute,
    private readonly injector: Injector,
    private readonly images: ImagesEndpoint,
    private readonly cd: ChangeDetectorRef
  ) { }

  get photosCount(): number {
    return this.imagesCount || this.galleryImages.length;
  }

  loadGalleryImages(onLoaded?: () => void): void {
    if (this.fullGalleryLoaded || this.galleryImages.length >= this.photosCount) {
      onLoaded?.();
      return;
    }

    if (this.loadingGalleryImages) {
      return;
    }

    this.loadingGalleryImages = true;
    this.images.getHotelImagesLinks([this.hotelId])
      .pipe(take(1))
      .subscribe({
        next: ({ imageResizesLinks }) => {
          const imageLinks = imageResizesLinks[this.hotelId];
          this.galleryImages = imageLinks ? Object.values(imageLinks).map(links => pickGalleryImage(links)) : this.galleryImages;
          this.fullGalleryLoaded = true;
          this.loadingGalleryImages = false;
          this.cd.markForCheck();
          setTimeout(() => onLoaded?.());
        },
        error: () => {
          this.loadingGalleryImages = false;
          this.cd.markForCheck();
        }
      });
  }

  showPreviousImage(event: MouseEvent): void {
    event.stopPropagation();
    this.navigateGallery('prev');
  }

  showNextImage(event: MouseEvent): void {
    event.stopPropagation();
    this.navigateGallery('next');
  }

  openMap(map: TemplateRef<any>): void {
    if (this.isDisplayableLocation()) {
      this.modalService.open(map, { centered: true, size: 'xl', injector: this.injector });
    }
  }

  ngOnChanges({ hotelId }: SimpleChanges): void {
    if (hotelId && hotelId.previousValue !== hotelId.currentValue) {
      this.fullGalleryLoaded = false;
      this.loadingGalleryImages = false;
      this.queryParams = this.patchParamsByHotelDestination(this.route.snapshot.queryParams as NormalizedParams);
      this.cd.markForCheck();
    }
  }

  private isDisplayableLocation(): boolean {
    if (this.location) {
      const { zoom, coordinates: [lat, lng] } = this.location;

      return zoom !== 0 || lat !== 0 || lng !== 0;
    }

    return false;
  }

  private navigateGallery(direction: 'prev' | 'next'): void {
    this.loadGalleryImages(() => {
      const arrowIndex = direction === 'prev' ? 0 : 1;
      const arrows = this.carousel?.nativeElement.querySelectorAll<HTMLElement>('.ngx-gallery-arrow');
      const arrow = arrows?.[arrowIndex];

      if (!arrow || arrow.classList.contains('ngx-gallery-disabled')) {
        return;
      }

      arrow.click();
    });
  }

  private patchParamsByHotelDestination({ searchId, did, dt, h, rs, ...rest }: NormalizedParams): Partial<NormalizedSearch> {
    return { ...rest, did: this.hotelId.toString(), dt: ToursSearch.DestinationType.Hotel };
  }
}
